import { useState } from "react";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import api from "@/api/client";
import { toast } from "sonner";

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", subject: "General", message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-[73px]">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
        <div className="mb-12">
          <p className="font-mono text-sm uppercase tracking-widest text-volt">Support</p>
          <h1 className="mt-2 font-display text-5xl font-bold uppercase tracking-tight text-white sm:text-6xl" data-testid="contact-heading">
            Contact Us
          </h1>
        </div>

        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <p className="text-slate-300" data-testid="contact-description">
              Questions about fitment, shipping, or a custom build? Drop us a line and we'll get back to you within 24 hours.
            </p>
            <div className="mt-8 space-y-6">
              <div className="flex items-start gap-4" data-testid="contact-email">
                <Mail className="mt-1 h-5 w-5 text-volt" />
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Email</p>
                  <p className="text-white">michaeladevoy@gmail.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-phone">
                <Phone className="mt-1 h-5 w-5 text-volt" />
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Phone</p>
                  <p className="text-white">734-258-4004</p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-location">
                <MapPin className="mt-1 h-5 w-5 text-volt" />
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Location</p>
                  <p className="text-white">Detroit, MI</p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="border border-white/10 bg-surface p-6 sm:p-8" data-testid="contact-form">
            <div className="space-y-4">
              <div>
                <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-slate-500">Name</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-white/10 bg-obsidian px-4 py-3 text-white placeholder:text-slate-600 focus:border-volt focus:outline-none"
                  placeholder="Your name"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-slate-500">Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-white/10 bg-obsidian px-4 py-3 text-white placeholder:text-slate-600 focus:border-volt focus:outline-none"
                  placeholder="you@example.com"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-slate-500">Subject</label>
                <select
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full border border-white/10 bg-obsidian px-4 py-3 text-white focus:border-volt focus:outline-none"
                  data-testid="contact-subject"
                >
                  <option>General</option>
                  <option>Fitment Question</option>
                  <option>Order Support</option>
                  <option>Custom Build</option>
                  <option>Wholesale</option>
                </select>
              </div>
              <div>
                <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-slate-500">Message</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full border border-white/10 bg-obsidian px-4 py-3 text-white placeholder:text-slate-600 focus:border-volt focus:outline-none"
                  placeholder="How can we help?"
                  data-testid="contact-message"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 bg-volt py-4 font-mono text-sm font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-white disabled:opacity-50"
                data-testid="contact-submit"
              >
                <Send className="h-4 w-4" />
                {loading ? "Sending..." : "Send Message"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
